// src/modules/sse-client.ts — Faza H.2 SSE client (Server-Sent Events)
// P3.2 strict TS migration (2026-04-30).
//
// Subscribe do /api/notifications/stream — long-running connection.
// Auto-reconnect z exponential backoff (1s, 2s, 4s, 8s, max 30s).
// 2026-09-24: JEDEN strumień na przeglądarkę — karty wybierają lidera (Web Locks), reszta
// dostaje zdarzenia przez BroadcastChannel. Szczegóły przy `LEADER_LOCK`.

import { isNotifCategoryMuted } from "./notif-prefs.ts";

// ── Types ────────────────────────────────────────────────────────────────

export type SSEEventType =
  | "heartbeat"
  | "invalidate"
  | "cache"
  | "info"
  | "success"
  | "warning"
  | "error";

export interface SSEEventExtra {
  domain?: string;
  thread_id?: string | number;
  account_id?: number;
  last_message_id?: string;
  [key: string]: unknown;
}

export interface SSEEvent {
  type: string;
  title?: string;
  body?: string;
  link?: string;
  domain?: string;
  extra?: SSEEventExtra;
  ts?: number;
  id?: string;
  // 2026-06-09: powiadomienia kierowane per-user. Gdy ustawione (truthy) → tylko
  // dla tego usera; gdy null/brak → broadcast (pokazuj wszystkim, jak dotąd).
  user_id?: number | string | null;
  // 2026-09-01: kategoria (invoices/orders/errors/…) — user wycisza w panelu
  // dzwonka; nadawana centralnie w core/notifications.py.
  category?: string;
}

export type InvalidateHandler = (extra: SSEEventExtra) => void;

// __sbSSE / __sbApiCache / __sbNotifications declared w src/types/window.d.ts (legacy)

interface AudioCtxCtor {
  new (): AudioContext;
}

declare global {
  interface Window {
    webkitAudioContext?: AudioCtxCtor;
  }
}

// ── State ────────────────────────────────────────────────────────────────

let _eventSource: EventSource | null = null;
let _reconnectAttempt = 0;
let _reconnectTimer: number | null = null;
let _connected = false;
let _lastSoundTs = 0;
// P8 (2026-09-15, PRZ-U5): tryb „?token= w adresie" USUNIĘTY. Do 09.2026 po błędzie
// cookie klient przechodził na URL z JWT (nginx access-log, breadcrumby Sentry, historia
// przeglądarki). Teraz przed każdym EventSource uzbrajamy cookie `sse_token` przez
// `POST /api/notifications/stream-session` (nagłówek Bearer, 204), a po błędzie cookie
// ponawiamy stream-session z tym samym wykładniczym backoffem co reconnect.
const STREAM_SESSION_PATH = "/api/notifications/stream-session";
let _armSeq = 0;

const MAX_RECONNECT_DELAY = 30_000;
const SOUND_MIN_INTERVAL_MS = 5000; // throttle dźwięku — max raz na 5s

// Mapa domain → callbacks
const _invalidateHandlers: Record<string, InvalidateHandler[]> = Object.create(null);

// ── Jeden strumień na przeglądarkę (2026-09-24) ─────────────────────────
// Właściciel: „zamuliło mi SellBase, innym działa, w incognito OK”. Każda karta trzymała
// własny EventSource, a kart bywało 20+ (diag_events: 23 sesje w 3 h) → pula połączeń
// profilu wyczerpana, każde kolejne żądanie czekało. Teraz karty jednego origin wybierają
// LIDERA przez Web Locks: tylko lider otwiera strumień, pozostałe (followerzy) dostają
// zdarzenia przez BroadcastChannel. Zamknięcie karty lidera zwalnia blokadę → kolejna karta
// w kolejce łączy się sama. Przejęcie: widoczna karta bez żadnej wiadomości od lidera przez
// 90 s (3× heartbeat serwera co 30 s) kradnie blokadę (`steal`) — zamrożona karta w tle nie
// odetnie tej, na którą użytkownik patrzy; stary lider po komunikacie „leader” oddaje strumień.
// Bez Web Locks / BroadcastChannel (stare przeglądarki, testy w vm) — jak dotąd: własny strumień.
type SSERole = "solo" | "leader" | "follower";
const LEADER_LOCK = "sellbase-sse-leader";
const CHANNEL_NAME = "sellbase-sse";
const TAKEOVER_SILENCE_MS = 90_000;
const TAKEOVER_CHECK_MS = 15_000;
const TAB_ID = _losoweId();
let _role: SSERole = "solo";
let _channel: BroadcastChannel | null = null;
let _leaderRelease: (() => void) | null = null;
let _leaderAbort: AbortController | null = null;
let _electionGen = 0;
let _lastLeaderMsgTs = 0;
let _takeoverTimer: number | null = null;
let _everConnected = false;

// ── Helpers ──────────────────────────────────────────────────────────────

function _getToken(): string {
  // Faza 5.9 v2 (2026-05-01): BUG FIX — token key mismatch.
  // PRZED: szukał "sellbase-token" (z myślnikiem) — który nigdy nie istniał.
  // SSE connect zawsze miał token="" → 401 → reconnect loop → brak instant push.
  // PO: ten sam fallback co api.js (orderflow_token primary, sellbase_token fallback).
  try {
    return (
      localStorage.getItem("orderflow_token") ||
      sessionStorage.getItem("orderflow_token") ||
      localStorage.getItem("sellbase_token") ||
      ""
    );
  } catch (_e: unknown) {
    return "";
  }
}

/**
 * 2026-06-09: id zalogowanego usera (dla filtra powiadomień per-user).
 * Sprawdza window.currentUser oraz __sbState.currentUser (oba używane w app).
 */
function _currentUserId(): number | string | null {
  try {
    const w = window as any;
    const u = w.currentUser || (w.__sbState && w.__sbState.currentUser)
      || (w.SellBaseApp && w.SellBaseApp.state && w.SellBaseApp.state.currentUser);
    return u && u.id != null ? u.id : null;
  } catch (_e: unknown) {
    return null;
  }
}

/**
 * Uzbrojenie cookie `sse_token` z bieżącego tokenu (HttpOnly, path /api/notifications/).
 * Zwraca true, gdy serwer potwierdził (204). Wołane przed każdym EventSource i po
 * odświeżeniu tokenu (auth-refresh-scheduler → `window.__sbSSE.rearm`).
 */
export async function armStreamSession(token?: string): Promise<boolean> {
  const t = token || _getToken();
  if (!t || typeof fetch !== "function") return false;
  try {
    const resp = await fetch(STREAM_SESSION_PATH, {
      method: "POST",
      headers: { Authorization: "Bearer " + t },
      credentials: "include",
    });
    return resp.status === 204 || resp.ok;
  } catch (e: unknown) {
    console.warn("[sse-client] stream-session failed:", e);
    return false;
  }
}

function _losoweId(): string {
  try {
    const c = (typeof crypto !== "undefined" ? crypto : null) as Crypto | null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch (_e: unknown) {
    // fallback niżej
  }
  return "t-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function _wieleKartWspierane(): boolean {
  try {
    const nav = (typeof navigator !== "undefined" ? navigator : null) as Navigator | null;
    return !!(nav && nav.locks && typeof nav.locks.request === "function" && typeof BroadcastChannel !== "undefined");
  } catch (_e: unknown) {
    return false;
  }
}

function _post(msg: Record<string, unknown>): void {
  if (!_channel) return;
  try {
    _channel.postMessage(msg);
  } catch (_e: unknown) {
    // kanał zamknięty — bez znaczenia
  }
}

function _otworzKanal(): void {
  if (_channel) return;
  try {
    _channel = new BroadcastChannel(CHANNEL_NAME);
    _channel.onmessage = (m: MessageEvent): void => _naWiadomoscKanalu(m.data);
  } catch (_e: unknown) {
    _channel = null;
  }
}

interface KanalMsg {
  k?: string;
  id?: string;
  ev?: SSEEvent;
  connected?: boolean;
}

function _naWiadomoscKanalu(d: unknown): void {
  const msg = d as KanalMsg | null;
  if (!msg || typeof msg !== "object" || !msg.k) return;
  if (msg.k === "event") {
    if (_role === "follower" && msg.ev) {
      _lastLeaderMsgTs = Date.now();
      _handleEvent(msg.ev);
    }
    return;
  }
  if (msg.k === "state") {
    if (_role === "follower") {
      _lastLeaderMsgTs = Date.now();
      _connected = !!msg.connected;
    }
    return;
  }
  if (msg.k === "leader") {
    if (msg.id === TAB_ID) return;
    if (_role === "leader") {
      console.info("[sse-client] inna karta została liderem — oddaję strumień");
      _zdegraduj();
    } else if (_role === "follower") {
      _lastLeaderMsgTs = Date.now();
    }
    return;
  }
  if (msg.k === "hello" && _role === "leader") {
    _post({ k: "state", connected: _connected });
  }
}

function _startElection(): void {
  if (_role !== "solo") return;
  _otworzKanal();
  if (!_channel) {
    _connectDirect();
    return;
  }
  _role = "follower";
  _lastLeaderMsgTs = Date.now();
  _post({ k: "hello" });
  _zglosSieDoBlokady(false);
  _uruchomNadzorPrzejecia();
}

function _zglosSieDoBlokady(steal: boolean): void {
  const gen = _electionGen;
  if (_leaderAbort) {
    try { _leaderAbort.abort(); } catch (_e: unknown) { /* noop */ }
    _leaderAbort = null;
  }
  const abort = steal ? null : new AbortController();
  _leaderAbort = abort;
  const opts: LockOptions = abort ? { signal: abort.signal } : { steal: true };
  let zadanie: Promise<unknown>;
  try {
    zadanie = navigator.locks.request(LEADER_LOCK, opts, () => new Promise<void>((resolve) => {
      if (gen !== _electionGen) { resolve(); return; }   // w międzyczasie disconnect
      _leaderRelease = resolve;
      _zostanLiderem();
    }));
  } catch (e: unknown) {
    console.warn("[sse-client] Web Locks niedostępne — własny strumień:", e);
    _role = "solo";
    _connectDirect();
    return;
  }
  void zadanie.catch((e: unknown) => {
    if (gen !== _electionGen) return;                 // celowy disconnect
    if (abort && abort.signal.aborted) return;        // nasz własny abort (steal / disconnect)
    if (_role === "leader") {                         // blokadę ukradła inna karta
      _zdegraduj();
      return;
    }
    if (_role === "follower") {
      console.warn("[sse-client] blokada lidera niedostępna — własny strumień:", e);
      _role = "solo";
      _connectDirect();
    }
  });
}

function _zostanLiderem(): void {
  _role = "leader";
  _post({ k: "leader", id: TAB_ID });
  console.info("[sse-client] ta karta jest liderem strumienia powiadomień");
  _connectDirect();
}

function _zdegraduj(): void {
  _zamknijStrumien();
  _role = "follower";
  _lastLeaderMsgTs = Date.now();
  const rel = _leaderRelease;
  _leaderRelease = null;
  if (rel) rel();
  _zglosSieDoBlokady(false);
}

function _zamknijStrumien(): void {
  ++_armSeq;
  if (_eventSource) {
    try {
      _eventSource.close();
    } catch (_e: unknown) {
      // best-effort close
    }
    _eventSource = null;
  }
  if (_reconnectTimer !== null) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
  _connected = false;
}

function _uruchomNadzorPrzejecia(): void {
  if (_takeoverTimer !== null || typeof window.setInterval !== "function") return;
  _takeoverTimer = window.setInterval(() => {
    if (_role !== "follower") return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (Date.now() - _lastLeaderMsgTs < TAKEOVER_SILENCE_MS) return;
    _lastLeaderMsgTs = Date.now();
    console.warn("[sse-client] lider strumienia milczy > 90 s — przejmuję strumień");
    _zglosSieDoBlokady(true);
  }, TAKEOVER_CHECK_MS);
  try {
    document.addEventListener("visibilitychange", () => {
      // Po powrocie do karty nie przejmuj od razu: odśwież licznik i zapytaj lidera o stan.
      if (document.visibilityState !== "hidden" && _role === "follower") {
        _lastLeaderMsgTs = Date.now();
        _post({ k: "hello" });
      }
    });
  } catch (_e: unknown) {
    // noop
  }
}

/**
 * Connect (or reconnect) to SSE stream.
 * Karta-lider (albo jedyna) otwiera strumień; follower nic nie robi — dostaje zdarzenia kanałem.
 */
export function connectSSE(): void {
  if (typeof window === "undefined" || typeof EventSource === "undefined") return;

  // Disable via ?nosse=1
  try {
    if (new URLSearchParams(window.location.search).has("nosse")) return;
  } catch (_e: unknown) {
    // best-effort URL parse
  }

  if (!_getToken()) {
    // Brak login — czekaj 5s i spróbuj ponownie
    _reconnectTimer = window.setTimeout(connectSSE, 5000);
    return;
  }
  if (_role === "follower") return;                       // strumień trzyma lider
  if (_role === "solo" && _wieleKartWspierane()) {        // pierwszy connect → wybór lidera
    _startElection();
    return;
  }
  _connectDirect();
}

/** Własny EventSource tej karty (tryb solo albo lider). */
function _connectDirect(): void {
  const token = _getToken();
  if (!token) {
    _reconnectTimer = window.setTimeout(connectSSE, 5000);
    return;
  }

  // Close existing connection
  if (_eventSource) {
    try {
      _eventSource.close();
    } catch (_e: unknown) {
      // best-effort close
    }
    _eventSource = null;
  }

  // Najpierw cookie (stream-session), dopiero potem strumień. Token NIGDY nie
  // trafia do adresu — stary `?token=` backend przyjmuje już tylko za flagą
  // przejściową SSE_QUERY_TOKEN_ALLOWED dla kart z poprzednim bundlem.
  const seq = ++_armSeq;
  void armStreamSession(token).then((ok) => {
    if (seq !== _armSeq) return;   // w międzyczasie ruszył nowszy connect
    if (!ok) {
      if (_reconnectAttempt < 3 || _reconnectAttempt % 10 === 0) {
        console.warn(`[sse-client] stream-session nieudane, ponawiam z backoffem (próba ${_reconnectAttempt + 1})`);
      }
      _scheduleReconnect();
      return;
    }
    _openEventSource();
  });
}

function _openEventSource(): void {
  // Cookie sse_token=<jwt>; HttpOnly; Secure; SameSite=Lax — wysyłane przy
  // EventSource z `withCredentials: true`. Adres BEZ tokenu.
  const url = "/api/notifications/stream";
  try {
    _eventSource = new EventSource(url, { withCredentials: true });
  } catch (e: unknown) {
    console.warn("[sse-client] EventSource failed:", e);
    _scheduleReconnect();
    return;
  }

  _eventSource.onopen = (): void => {
    _connected = true;
    _reconnectAttempt = 0;
    const ponowne = _everConnected;
    _everConnected = true;
    console.info("[sse-client] connected");
    if (_role === "leader") _post({ k: "state", connected: true });
    if (ponowne) {
      // Strumień wrócił po przerwie (restart serwera = wdrożenie): dobry moment, by sprawdzić
      // nową wersję aplikacji (sw-register nasłuchuje i woła reg.update()).
      try {
        window.dispatchEvent(new CustomEvent("sb:sse-reconnected"));
      } catch (_e: unknown) {
        // noop
      }
      // S-91b: wersje danych z przerwy przepadły — jedno odświeżenie wszystkich liczników
      // (także w kartach-followerach, które nie mają własnego strumienia).
      const sync: SSEEvent = { type: "cache", extra: { domena: "*", rev: 0, resync: true } };
      _handleEvent(sync);
      if (_role === "leader") _post({ k: "event", ev: sync });
    }
  };

  _eventSource.onmessage = (e: MessageEvent): void => {
    try {
      const event = JSON.parse(e.data as string) as SSEEvent;
      _handleEvent(event);
      if (_role === "leader") _post({ k: "event", ev: event });
    } catch (err: unknown) {
      console.warn("[sse-client] parse error:", err);
    }
  };

  _eventSource.onerror = (_e: Event): void => {
    const wasConnected = _connected;
    _connected = false;
    if (_role === "leader" && wasConnected) _post({ k: "state", connected: false });
    if (_eventSource && _eventSource.readyState === EventSource.CLOSED) {
      // Błąd PRZED onopen = cookie nie zadziałało (wygasłe, brak, blokada) → reconnect
      // przechodzi przez stream-session (ponowne uzbrojenie) z backoffem. Bez
      // przełączania na adres z tokenem.
      if (!wasConnected) {
        if (_reconnectAttempt < 3 || _reconnectAttempt % 10 === 0) {
          console.warn("[sse-client] cookie-auth failed before connect — re-arming stream-session with backoff");
        }
      } else if (_reconnectAttempt < 3 || _reconnectAttempt % 10 === 0) {
        // log tylko pierwsze próby i co 10. — pętla reconnectów zalewała konsolę/diag
        console.warn(`[sse-client] connection closed, reconnecting... (próba ${_reconnectAttempt + 1})`);
      }
      _scheduleReconnect();
    }
  };
}

/**
 * Krótki dźwięk powiadomienia (Web Audio API — bez sample, generowany ton).
 * Throttle: max raz na 5s.
 */
function _playNotifSound(): void {
  const now = Date.now();
  if (now - _lastSoundTs < SOUND_MIN_INTERVAL_MS) return;
  _lastSoundTs = now;
  try {
    const Ctx: AudioCtxCtor | undefined =
      (window.AudioContext as AudioCtxCtor | undefined) || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880; // A5
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
    setTimeout(() => {
      try {
        void ctx.close();
      } catch (_e: unknown) {
        // ignore
      }
    }, 250);
  } catch (_e: unknown) {
    // best-effort
  }
}

// S-91b/E4: wersje danych domen (liczniki). Backend wysyła je jako `invalidate` z
// `extra.domain = "cache:<domena>"` (stare bundle traktują to jako no-op: bez dzwonka i dźwięku).
// Strażnik: per `firma|domena` tylko rosnący `rev` (echo kanału lidera, odtworzenie, cofnięcie).
const _cacheRevs: Record<string, number> = Object.create(null);

function _obsluzCache(event: SSEEvent): void {
  const ex = (event.extra || {}) as SSEEventExtra;
  const domena = String(ex.domena || "");
  if (!domena) return;
  const rev = Number(ex.rev);
  const resync = ex.resync === true;
  if (!resync) {
    if (!Number.isFinite(rev)) return;
    const klucz = String(ex.firma ?? "") + "|" + domena;
    const poprzedni = _cacheRevs[klucz];
    if (poprzedni !== undefined && rev <= poprzedni) return;
    _cacheRevs[klucz] = rev;
  }
  try {
    window.dispatchEvent(
      new CustomEvent("sb:cache", {
        detail: { domena, rev: Number.isFinite(rev) ? rev : 0, firma: ex.firma, resync },
      }),
    );
  } catch (_e: unknown) {
    // noop
  }
}

function _handleEvent(event: SSEEvent): void {
  if (!event || !event.type) return;

  // Special: heartbeat — ignore
  if (event.type === "heartbeat") return;

  // S-91b: wersja danych (przed filtrem użytkownika, kategorii, dzwonkiem i dźwiękiem).
  if (event.type === "cache") {
    _obsluzCache(event);
    return;
  }

  // Special: invalidate cache event (supports event.domain OR event.extra.domain)
  if (event.type === "invalidate") {
    const domain = event.domain || (event.extra && event.extra.domain);
    if (typeof domain === "string" && domain.startsWith("cache:")) {
      _obsluzCache(event);
      return;
    }
    if (domain) {
      const apiCache = window.__sbApiCache;
      if (apiCache && typeof apiCache.invalidateDomain === "function") {
        apiCache.invalidateDomain(domain);
        console.info("[sse-client] cache invalidated for domain:", domain);
      }
      // Auto-refresh aktualnego view (handlers dostają `extra`)
      const handlers = _invalidateHandlers[domain] || [];
      for (const fn of handlers) {
        try {
          fn(event.extra || {});
        } catch (e: unknown) {
          console.warn("[sse-client] handler failed:", e);
        }
      }
    }
    return;
  }

  // 2026-06-09: filtr per-user. Gdy event.user_id ustawione (truthy) i NIE równa
  // się id zalogowanego usera → POMIŃ (powiadomienie skierowane do kogoś innego).
  // Broadcast (user_id null/brak) pokazujemy jak dotąd.
  if (event.user_id) {
    const me = _currentUserId();
    // Porównanie luźne (string vs number) — backend może wysłać id jako number,
    // a front trzymać string (lub odwrotnie).
    if (me == null || String(event.user_id) !== String(me)) {
      return;
    }
  }

  // 2026-09-01: wyciszone kategorie (preferencje usera w panelu dzwonka).
  // Invalidate nigdy tu nie dociera (return wyżej), więc cache jest bezpieczny.
  const _kat = String(event.category || "");
  if (_kat && isNotifCategoryMuted(_kat)) return;

  // Default: push do NotificationCenter
  const notifs = window.__sbNotifications;
  if (notifs && typeof notifs.push === "function") {
    notifs.push({
      type: event.type,
      title: event.title || "",
      body: event.body || "",
      link: event.link,
    });
  }

  // Toast dla error/warning
  if ((event.type === "error" || event.type === "warning") && typeof window.showToast === "function") {
    try {
      const toastType = event.type as "error" | "warning";
      window.showToast(event.title || event.body || "Powiadomienie", toastType);
    } catch (_e: unknown) {
      // ignore
    }
  }

  // Dźwięk (tylko gdy tab aktywny)
  if (typeof document !== "undefined" && !document.hidden) {
    _playNotifSound();
  }
}

/**
 * Rejestruje callback wywoływany przy SSE invalidate dla danej domain.
 */
export function onInvalidate(domain: string, fn: InvalidateHandler): () => void {
  if (!_invalidateHandlers[domain]) _invalidateHandlers[domain] = [];
  _invalidateHandlers[domain].push(fn);
  return (): void => {
    const list = _invalidateHandlers[domain];
    if (!list) return;
    const i = list.indexOf(fn);
    if (i >= 0) list.splice(i, 1);
  };
}

function _scheduleReconnect(): void {
  if (_reconnectTimer !== null) clearTimeout(_reconnectTimer);
  _reconnectAttempt++;
  // Exponential backoff: 1s, 2s, 4s, 8s, ..., max 30s.
  // 2026-07-18: po 8 porażkach pod rząd cap rośnie do 5 min — nocna karta z wygasłą
  // sesją potrafiła młócić reconnect(401) co ≤30s przez całą noc (diag: x353 u jednego
  // usera) = setki zbędnych żądań + spam w kolektorze. Po powrocie usera pierwszy
  // fetch odświeży token, a najbliższy reconnect (≤5 min) podniesie SSE z powrotem.
  const cap = _reconnectAttempt > 8 ? 300_000 : MAX_RECONNECT_DELAY;
  const delay = Math.min(1000 * Math.pow(2, _reconnectAttempt - 1), cap);
  _reconnectTimer = window.setTimeout(connectSSE, delay);
}

/**
 * Manually disconnect (logout, debug).
 */
export function disconnectSSE(): void {
  // Uzbrajanie cookie w toku (stream-session) nie może po wylogowaniu otworzyć strumienia
  // (`_zamknijStrumien` podbija _armSeq). Lider oddaje blokadę → następna karta łączy się sama.
  ++_electionGen;
  _zamknijStrumien();
  if (_leaderAbort) {
    try { _leaderAbort.abort(); } catch (_e: unknown) { /* noop */ }
    _leaderAbort = null;
  }
  const rel = _leaderRelease;
  _leaderRelease = null;
  if (rel) rel();
  if (_role === "leader") _post({ k: "state", connected: false });
  _role = "solo";
  if (_takeoverTimer !== null) {
    clearInterval(_takeoverTimer);
    _takeoverTimer = null;
  }
}

/**
 * Status — dla debug.
 */
export function isConnected(): boolean {
  return _connected;
}

// Auto-connect po DOM ready + login
// Audyt 2026-07-16: 2000ms → 600ms. Token jest w localStorage od razu; wcześniejszy
// connect = live-update od ~0,6s zamiast 2s. Handlery (sse-auto-refresh _setup) rejestrują
// się na 300ms, czyli PRZED connectem — brak okna, w którym event ginie bez handlera.
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => setTimeout(connectSSE, 600));
  } else {
    setTimeout(connectSSE, 600);
  }
  window.__sbSSE = {
    connect: connectSSE,
    disconnect: disconnectSSE,
    isConnected,
    onInvalidate,
    // Follower nie trzyma strumienia — cookie uzbraja tylko karta z EventSource.
    rearm: () => { if (_role !== "follower") void armStreamSession(); },
    role: () => _role,
  };
}
